// =============================================================================
// Canvas Core Constants
// =============================================================================

import type { SizePreset, AspectRatio, CanvasState, ImageFilters, BlendMode, CanvasTemplate } from './types';

// -----------------------------------------------------------------------------
// Size Presets
// -----------------------------------------------------------------------------

export const SIZE_PRESETS: SizePreset[] = [
  // Social
  { id: 'instagram_post', name: 'Instagram Post', width: 1080, height: 1350, category: 'social' },
  { id: 'instagram_square', name: 'Instagram Square', width: 1080, height: 1080, category: 'social' },
  { id: 'instagram_story', name: 'Instagram Story', width: 1080, height: 1920, category: 'social' },
  { id: 'facebook_event', name: 'Facebook Event', width: 1920, height: 1005, category: 'social',
    promptAddition: 'Facebook event cover photo with event details clearly visible.' },
  { id: 'twitter_post', name: 'Twitter/X Post', width: 1200, height: 675, category: 'social',
    promptAddition: 'Twitter post image optimized for timeline display.' },

  // Print
  { id: 'flyer_portrait', name: 'Flyer Portrait', width: 1080, height: 1920, category: 'print' },
  { id: 'flyer_landscape', name: 'Flyer Landscape', width: 1920, height: 1080, category: 'print' },
  { id: 'poster_a3', name: 'Poster A3', width: 3508, height: 4961, category: 'print' },

  // Banner / Display
  { id: 'led_banner', name: 'LED Banner', width: 1920, height: 1080, category: 'banner' },
  { id: '4k_horizontal', name: '4K Horizontal', width: 3840, height: 2160, category: 'banner' },
  { id: '4k_vertical', name: '4K Vertical', width: 2160, height: 3840, category: 'banner' },

  // Streaming (Pro+)
  { id: 'streaming_lower_third', name: 'Streaming Lower Third', width: 1920, height: 540, category: 'streaming',
    promptAddition: 'Design a lower-third overlay with transparent background for live streaming.',
    requiredTier: 'pro', icon: 'monitor' },
  { id: 'stream_overlay', name: 'Stream Overlay (16:9)', width: 1920, height: 1080, category: 'streaming',
    promptAddition: 'Full-screen streaming overlay with transparent areas for webcam and content.',
    requiredTier: 'pro', icon: 'tv' },

  // Marketing (Pro+)
  { id: 'sticker_pack', name: 'Sticker', width: 512, height: 512, category: 'marketing',
    promptAddition: 'Create a sticker-style graphic with die-cut edges and bold design.',
    requiredTier: 'pro', icon: 'sticker' },
  { id: 'pitch_deck_slide', name: 'Pitch Deck Slide', width: 1920, height: 1080, category: 'marketing',
    promptAddition: 'Professional presentation slide with clean layout and data visualization.',
    requiredTier: 'pro', icon: 'presentation' },
  { id: 'email_header', name: 'Email Header', width: 600, height: 200, category: 'marketing',
    promptAddition: 'Email-safe header banner with bold branding, 600px wide.',
    requiredTier: 'pro', icon: 'mail' },
  { id: 'ticket_design', name: 'Ticket Design', width: 1050, height: 450, category: 'marketing',
    promptAddition: 'Event ticket design with clear event info areas and professional layout.',
    requiredTier: 'pro', icon: 'ticket' },
];

// -----------------------------------------------------------------------------
// Aspect Ratios
// -----------------------------------------------------------------------------

export const CANVAS_ASPECT_RATIOS: AspectRatio[] = [
  { id: '1:1', label: '1:1 Square', width: 1080, height: 1080 },
  { id: '4:5', label: '4:5', width: 1080, height: 1350 },
  { id: '3:4', label: '3:4', width: 1080, height: 1440 },
  { id: '9:16', label: '9:16 Portrait', width: 1080, height: 1920 },
  { id: '16:9', label: '16:9 Landscape', width: 1920, height: 1080 },
  { id: '2:3', label: '2:3', width: 1080, height: 1620 },
];

// -----------------------------------------------------------------------------
// Grid & Snap
// -----------------------------------------------------------------------------

export const DEFAULT_GRID_SIZE = 20;
export const SNAP_THRESHOLD = 8;
export const ROTATION_SNAP_DEGREES = 15;

// -----------------------------------------------------------------------------
// Zoom
// -----------------------------------------------------------------------------

export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 5;
export const DEFAULT_ZOOM = 1;
export const ZOOM_STEP = 0.1;

// -----------------------------------------------------------------------------
// History
// -----------------------------------------------------------------------------

export const MAX_HISTORY_STATES = 50;

// -----------------------------------------------------------------------------
// Default Canvas State
// -----------------------------------------------------------------------------

export const DEFAULT_BACKGROUND_COLOR = '#1a1a2e';

export const DEFAULT_CANVAS_STATE: CanvasState = {
  width: 1080,
  height: 1350,
  backgroundColor: DEFAULT_BACKGROUND_COLOR,
  layers: [
    {
      id: 'background',
      type: 'background',
      name: 'Background',
      visible: true,
      locked: true,
      x: 0,
      y: 0,
      width: 1080,
      height: 1350,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      opacity: 1,
      z_index: 0,
      blendMode: 'normal',
      fillOpacity: 1,
      effects: [],
      fill: { type: 'solid', color: DEFAULT_BACKGROUND_COLOR },
      entity_tokens: [],
    },
  ],
};

// -----------------------------------------------------------------------------
// Default Image Filters
// -----------------------------------------------------------------------------

export const DEFAULT_IMAGE_FILTERS: ImageFilters = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  blur: 0,
  grayscale: false,
  sepia: false,
  invert: false,
  hueRotation: 0,
};

// -----------------------------------------------------------------------------
// Blending Modes (for UI dropdowns)
// -----------------------------------------------------------------------------

export const BLEND_MODE_GROUPS: { label: string; modes: { value: BlendMode; label: string }[] }[] = [
  {
    label: 'Normal',
    modes: [
      { value: 'normal', label: 'Normal' },
      { value: 'dissolve', label: 'Dissolve' },
    ],
  },
  {
    label: 'Darken',
    modes: [
      { value: 'darken', label: 'Darken' },
      { value: 'multiply', label: 'Multiply' },
      { value: 'color-burn', label: 'Color Burn' },
      { value: 'linear-burn', label: 'Linear Burn' },
      { value: 'darker-color', label: 'Darker Color' },
    ],
  },
  {
    label: 'Lighten',
    modes: [
      { value: 'lighten', label: 'Lighten' },
      { value: 'screen', label: 'Screen' },
      { value: 'color-dodge', label: 'Color Dodge' },
      { value: 'linear-dodge', label: 'Linear Dodge (Add)' },
      { value: 'lighter-color', label: 'Lighter Color' },
    ],
  },
  {
    label: 'Contrast',
    modes: [
      { value: 'overlay', label: 'Overlay' },
      { value: 'soft-light', label: 'Soft Light' },
      { value: 'hard-light', label: 'Hard Light' },
      { value: 'vivid-light', label: 'Vivid Light' },
      { value: 'linear-light', label: 'Linear Light' },
      { value: 'pin-light', label: 'Pin Light' },
      { value: 'hard-mix', label: 'Hard Mix' },
    ],
  },
  {
    label: 'Inversion',
    modes: [
      { value: 'difference', label: 'Difference' },
      { value: 'exclusion', label: 'Exclusion' },
      { value: 'subtract', label: 'Subtract' },
      { value: 'divide', label: 'Divide' },
    ],
  },
  {
    label: 'Component',
    modes: [
      { value: 'hue', label: 'Hue' },
      { value: 'saturation', label: 'Saturation' },
      { value: 'color', label: 'Color' },
      { value: 'luminosity', label: 'Luminosity' },
    ],
  },
];

// -----------------------------------------------------------------------------
// Layer Defaults
// -----------------------------------------------------------------------------

export const DEFAULT_TEXT_STYLE = {
  fontFamily: 'Inter',
  fontSize: 48,
  fill: '#ffffff',
  fontWeight: 'normal',
  fontStyle: 'normal' as const,
  textAlign: 'center' as const,
  lineHeight: 1.2,
  charSpacing: 0,
};

export const DEFAULT_SHAPE_FILL = { type: 'solid' as const, color: '#6366f1' };
export const DEFAULT_STROKE_COLOR = '#ffffff';
export const DEFAULT_STROKE_WIDTH = 0;

// -----------------------------------------------------------------------------
// Canvas Templates (for Start Wizard)
// -----------------------------------------------------------------------------

export const CANVAS_TEMPLATES: CanvasTemplate[] = [
  {
    id: 'blank',
    name: 'Blank Canvas',
    description: 'Start from scratch',
    colors: ['#1a1a2e'],
    backgroundFill: { type: 'solid', color: '#1a1a2e' },
    layers: [],
  },
  {
    id: 'neon-nightlife',
    name: 'Neon Nightlife',
    description: 'Electric vibes for club events',
    colors: ['#0f0c29', '#302b63', '#24243e'],
    backgroundFill: {
      type: 'gradient',
      gradient: {
        type: 'linear',
        angle: 180,
        stops: [
          { offset: 0, color: '#0f0c29' },
          { offset: 0.5, color: '#302b63' },
          { offset: 1, color: '#24243e' },
        ],
      },
    },
    layers: [
      {
        type: 'text', name: 'Event Name',
        relX: 0.1, relY: 0.15, relW: 0.8, relH: 0.12,
        text_content: 'EVENT NAME',
        text_style: { fontFamily: 'Inter', fontSize: 72, fill: '#ffffff', fontWeight: 'bold', textAlign: 'center', lineHeight: 1.1, charSpacing: 80 },
      },
      {
        type: 'shape', name: 'Neon Line',
        relX: 0.2, relY: 0.29, relW: 0.6, relH: 0.004,
        shapeType: 'rectangle', fill: '#00d4ff', opacity: 0.8,
      },
      {
        type: 'text', name: 'Tagline',
        relX: 0.15, relY: 0.32, relW: 0.7, relH: 0.06,
        text_content: 'FEATURING SPECIAL GUESTS',
        text_style: { fontFamily: 'Inter', fontSize: 24, fill: '#00d4ff', fontWeight: 'normal', textAlign: 'center', lineHeight: 1.3, charSpacing: 200 },
      },
      {
        type: 'text', name: 'Date & Time',
        relX: 0.1, relY: 0.78, relW: 0.8, relH: 0.06,
        text_content: 'SATURDAY • 10PM - 4AM',
        text_style: { fontFamily: 'Inter', fontSize: 28, fill: '#e0e0e0', fontWeight: '600', textAlign: 'center', lineHeight: 1.2, charSpacing: 100 },
      },
      {
        type: 'text', name: 'Venue',
        relX: 0.1, relY: 0.85, relW: 0.8, relH: 0.05,
        text_content: 'VENUE NAME • CITY',
        text_style: { fontFamily: 'Inter', fontSize: 20, fill: '#888888', fontWeight: 'normal', textAlign: 'center', lineHeight: 1.2, charSpacing: 150 },
      },
    ],
  },
  {
    id: 'minimal-elegant',
    name: 'Minimal Elegant',
    description: 'Clean and sophisticated',
    colors: ['#1a1a2e', '#d4af37', '#f5f5f5'],
    backgroundFill: { type: 'solid', color: '#0d0d0d' },
    layers: [
      {
        type: 'shape', name: 'Gold Accent Top',
        relX: 0.35, relY: 0.12, relW: 0.3, relH: 0.003,
        shapeType: 'rectangle', fill: '#d4af37',
      },
      {
        type: 'text', name: 'Event Name',
        relX: 0.1, relY: 0.18, relW: 0.8, relH: 0.1,
        text_content: 'Event Name',
        text_style: { fontFamily: 'Georgia', fontSize: 64, fill: '#f5f5f5', fontWeight: 'normal', textAlign: 'center', lineHeight: 1.15, charSpacing: 40 },
      },
      {
        type: 'shape', name: 'Gold Accent Bottom',
        relX: 0.35, relY: 0.30, relW: 0.3, relH: 0.003,
        shapeType: 'rectangle', fill: '#d4af37',
      },
      {
        type: 'text', name: 'Details',
        relX: 0.15, relY: 0.34, relW: 0.7, relH: 0.05,
        text_content: 'An Exclusive Evening',
        text_style: { fontFamily: 'Georgia', fontSize: 22, fill: '#d4af37', fontWeight: 'normal', fontStyle: 'italic', textAlign: 'center', lineHeight: 1.3, charSpacing: 80 },
      },
      {
        type: 'text', name: 'Date',
        relX: 0.1, relY: 0.82, relW: 0.8, relH: 0.05,
        text_content: 'Date • Time • Venue',
        text_style: { fontFamily: 'Georgia', fontSize: 18, fill: '#999999', fontWeight: 'normal', textAlign: 'center', lineHeight: 1.3, charSpacing: 120 },
      },
    ],
  },
  {
    id: 'retro-vintage',
    name: 'Retro Vintage',
    description: 'Throwback aesthetics',
    colors: ['#2d1b0e', '#d4763c', '#f2d6a2'],
    backgroundFill: {
      type: 'gradient',
      gradient: {
        type: 'linear',
        angle: 160,
        stops: [
          { offset: 0, color: '#2d1b0e' },
          { offset: 1, color: '#4a2c17' },
        ],
      },
    },
    layers: [
      {
        type: 'shape', name: 'Border',
        relX: 0.04, relY: 0.03, relW: 0.92, relH: 0.94,
        shapeType: 'rectangle', fill: { type: 'solid', color: 'transparent' },
        opacity: 0.6, cornerRadius: 8,
      },
      {
        type: 'text', name: 'Presents',
        relX: 0.1, relY: 0.12, relW: 0.8, relH: 0.04,
        text_content: '— PRESENTS —',
        text_style: { fontFamily: 'Georgia', fontSize: 18, fill: '#d4763c', fontWeight: 'normal', textAlign: 'center', lineHeight: 1.2, charSpacing: 300 },
      },
      {
        type: 'text', name: 'Event Name',
        relX: 0.08, relY: 0.18, relW: 0.84, relH: 0.14,
        text_content: 'EVENT\nNAME',
        text_style: { fontFamily: 'Georgia', fontSize: 72, fill: '#f2d6a2', fontWeight: 'bold', textAlign: 'center', lineHeight: 1.05, charSpacing: 60 },
      },
      {
        type: 'shape', name: 'Divider',
        relX: 0.3, relY: 0.35, relW: 0.4, relH: 0.002,
        shapeType: 'rectangle', fill: '#d4763c', opacity: 0.5,
      },
      {
        type: 'text', name: 'Date & Venue',
        relX: 0.1, relY: 0.83, relW: 0.8, relH: 0.06,
        text_content: 'Saturday Night • The Venue',
        text_style: { fontFamily: 'Georgia', fontSize: 20, fill: '#d4763c', fontWeight: 'normal', textAlign: 'center', lineHeight: 1.3, charSpacing: 60 },
      },
    ],
  },
  {
    id: 'industrial-grunge',
    name: 'Industrial Grunge',
    description: 'Raw underground style',
    colors: ['#1a1a1a', '#4a4a4a', '#ef5228'],
    backgroundFill: { type: 'solid', color: '#111111' },
    layers: [
      {
        type: 'text', name: 'Event Name',
        relX: 0.05, relY: 0.08, relW: 0.9, relH: 0.18,
        text_content: 'EVENT\nNAME',
        text_style: { fontFamily: 'Inter', fontSize: 84, fill: '#ffffff', fontWeight: '900', textAlign: 'left', lineHeight: 0.95, charSpacing: -20 },
      },
      {
        type: 'shape', name: 'Red Bar',
        relX: 0, relY: 0.30, relW: 0.45, relH: 0.008,
        shapeType: 'rectangle', fill: '#ef5228',
      },
      {
        type: 'text', name: 'Details',
        relX: 0.05, relY: 0.33, relW: 0.6, relH: 0.05,
        text_content: 'UNDERGROUND SESSIONS',
        text_style: { fontFamily: 'Inter', fontSize: 20, fill: '#ef5228', fontWeight: '700', textAlign: 'left', lineHeight: 1.2, charSpacing: 200 },
      },
      {
        type: 'text', name: 'Date & Location',
        relX: 0.05, relY: 0.88, relW: 0.9, relH: 0.05,
        text_content: 'SAT 23:00 — WAREHOUSE DISTRICT',
        text_style: { fontFamily: 'Inter', fontSize: 16, fill: '#666666', fontWeight: '600', textAlign: 'left', lineHeight: 1.2, charSpacing: 150 },
      },
    ],
  },
  {
    id: 'futuristic-tech',
    name: 'Futuristic Tech',
    description: 'Sci-fi inspired design',
    colors: ['#0a0a2e', '#00d4ff', '#7b2ff7'],
    backgroundFill: {
      type: 'gradient',
      gradient: {
        type: 'linear',
        angle: 135,
        stops: [
          { offset: 0, color: '#0a0a2e' },
          { offset: 0.6, color: '#0d1140' },
          { offset: 1, color: '#1a0a3e' },
        ],
      },
    },
    layers: [
      {
        type: 'shape', name: 'Accent Line',
        relX: 0.08, relY: 0.12, relW: 0.15, relH: 0.003,
        shapeType: 'rectangle', fill: '#00d4ff',
      },
      {
        type: 'text', name: 'Label',
        relX: 0.08, relY: 0.14, relW: 0.5, relH: 0.03,
        text_content: 'IMMERSIVE EXPERIENCE',
        text_style: { fontFamily: 'Inter', fontSize: 14, fill: '#00d4ff', fontWeight: '500', textAlign: 'left', lineHeight: 1.2, charSpacing: 300 },
      },
      {
        type: 'text', name: 'Event Name',
        relX: 0.08, relY: 0.20, relW: 0.84, relH: 0.12,
        text_content: 'EVENT\nNAME',
        text_style: { fontFamily: 'Inter', fontSize: 72, fill: '#ffffff', fontWeight: '800', textAlign: 'left', lineHeight: 1.0, charSpacing: 20 },
      },
      {
        type: 'shape', name: 'Purple Glow Bar',
        relX: 0.08, relY: 0.34, relW: 0.35, relH: 0.005,
        shapeType: 'rectangle', fill: '#7b2ff7', opacity: 0.8,
      },
      {
        type: 'text', name: 'Date & Venue',
        relX: 0.08, relY: 0.86, relW: 0.84, relH: 0.05,
        text_content: '2025.01.15 — VENUE NAME',
        text_style: { fontFamily: 'Inter', fontSize: 16, fill: '#7b2ff7', fontWeight: '500', textAlign: 'left', lineHeight: 1.2, charSpacing: 150 },
      },
    ],
  },
];
